/**
 * Tech Stack Recommendation Engine
 *
 * Intelligent scoring algorithm that matches user requirements with technology options
 */

import { TechnologyOption } from './tech-stack-types';

export interface UserRequirements {
  // Organization Context
  teamSize: 'startup' | 'small' | 'medium' | 'large' | 'enterprise';
  industry: string;

  // Technical Requirements
  scalabilityNeeds: 'low' | 'medium' | 'high' | 'very-high';
  performanceNeeds: 'low' | 'medium' | 'high' | 'very-high';

  // Budget & Resources
  budget: 'minimal' | 'moderate' | 'substantial' | 'enterprise';
  teamExpertise: 'beginner' | 'intermediate' | 'advanced' | 'expert';

  // Operational Preferences
  hostingPreference: 'self-hosted' | 'cloud' | 'managed' | 'no-preference';
  maintenanceCapability: 'minimal' | 'moderate' | 'high';

  // Compliance Requirements
  gdprRequired: boolean;
  hipaaRequired: boolean;
  soc2Required: boolean;
  pciRequired: boolean;

  // Strategic Considerations
  vendorLockInTolerance: 'none' | 'low' | 'medium' | 'high';
  openSourcePreference: 'required' | 'preferred' | 'neutral' | 'commercial-ok';
  innovationVsStability: 'bleeding-edge' | 'modern' | 'stable' | 'conservative';

  // Specific Use Cases
  useCases: string[];
  mustHaveFeatures: string[];
  niceToHaveFeatures: string[];

  // New Enhanced Fields (optional for backward compatibility)
  priorityWeights?: Record<string, number>;
  expertiseByCategory?: Record<string, 'none' | 'beginner' | 'intermediate' | 'advanced' | 'expert'>;
  timeline?: 'very-urgent' | 'urgent' | 'moderate' | 'flexible';
  currentStack?: string[];
  complianceDetails?: Record<string, string[]>;
  multiRegion?: boolean;
  regions?: string[];
  transactionVolume?: 'low' | 'medium' | 'high' | 'very-high';
}

export interface ScoredTechnology extends TechnologyOption {
  score: number;
  scoreBreakdown: {
    compliance: number;
    technical: number;
    operational: number;
    cost: number;
    ecosystem: number;
    strategic: number;
  };
  scoreRationales: {
    compliance: string;
    technical: string;
    operational: string;
    cost: string;
    ecosystem: string;
    strategic: string;
  };
  matchReasons: string[];
  concerns: string[];
  rank: number;
}

export interface RunnerUp {
  technology: ScoredTechnology;
  reason: string;
  scoreComparison: string;
  alternativeTo: string;
}

export interface AlternativeStack {
  name: string;
  description: string;
  technologies: ScoredTechnology[];
  tradeoffs: string[];
  estimatedMonthlyCost?: string;
  whenToChoose: string;
  vsRecommended: {
    costDelta: string;
    performanceDelta: string;
    complexityDelta: string;
  };
}

export interface RiskAssessment {
  level: 'low' | 'medium' | 'high';
  category: string;
  description: string;
  mitigation: string;
}

export interface EnhancedRecommendationResult {
  primary: ScoredTechnology[];
  runnersUp: RunnerUp[];
  alternativeStacks: AlternativeStack[];
  risks: RiskAssessment[];
  metadata: {
    totalEvaluated: number;
    filteringCriteria: string[];
    scoreRange: { min: number; max: number };
  };
}

/**
 * Score a technology against user requirements
 */
export function scoreTechnology(
  tech: TechnologyOption,
  requirements: UserRequirements
): ScoredTechnology {
  const complianceResult = scoreCompliance(tech, requirements);
  const technicalResult = scoreTechnical(tech, requirements);
  const operationalResult = scoreOperational(tech, requirements);
  const costResult = scoreCost(tech, requirements);
  const ecosystemResult = scoreEcosystem(tech, requirements);
  const strategicResult = scoreStrategic(tech, requirements);

  const scoreBreakdown = {
    compliance: complianceResult.score,
    technical: technicalResult.score,
    operational: operationalResult.score,
    cost: costResult.score,
    ecosystem: ecosystemResult.score,
    strategic: strategicResult.score,
  };

  const scoreRationales = {
    compliance: complianceResult.rationale,
    technical: technicalResult.rationale,
    operational: operationalResult.rationale,
    cost: costResult.rationale,
    ecosystem: ecosystemResult.rationale,
    strategic: strategicResult.rationale,
  };

  // Weighted average (adjust weights based on user priorities if provided)
  let weights = {
    compliance: 0.25, // High weight - must meet requirements
    technical: 0.20,
    operational: 0.15,
    cost: 0.15,
    ecosystem: 0.15,
    strategic: 0.10,
  };

  // Apply user-defined priority weights if available
  if (requirements.priorityWeights) {
    const userWeights = requirements.priorityWeights;
    const totalUserWeight = Object.values(userWeights).reduce((sum, w) => sum + w, 0);

    if (totalUserWeight > 0) {
      // Normalize user weights to sum to 1.0
      weights = {
        compliance: (userWeights['security-compliance'] || 3) / totalUserWeight,
        technical: (userWeights['performance'] || 3) / totalUserWeight,
        operational: (userWeights['developer-experience'] || 3) / totalUserWeight,
        cost: (userWeights['cost-efficiency'] || 3) / totalUserWeight,
        ecosystem: (userWeights['time-to-market'] || 3) / totalUserWeight,
        strategic: (userWeights['vendor-lock-in-avoidance'] || 2) / totalUserWeight,
      };
    }
  }

  const totalScore = Object.entries(scoreBreakdown).reduce(
    (sum, [key, value]) => sum + value * weights[key as keyof typeof weights],
    0
  );

  const { matchReasons, concerns } = generateInsights(tech, requirements, scoreBreakdown);

  return {
    ...tech,
    score: Math.round(totalScore * 100) / 100,
    scoreBreakdown,
    scoreRationales,
    matchReasons,
    concerns,
    rank: 0, // Will be set after sorting
  };
}

/**
 * Score compliance match
 */
function scoreCompliance(tech: TechnologyOption, req: UserRequirements): { score: number; rationale: string } {
  let score = 1.0;
  const reasons: string[] = [];
  const missing: string[] = [];

  // Hard requirements - must match or score drops significantly
  if (req.gdprRequired) {
    if (tech.compliance.gdpr) {
      reasons.push('GDPR compliant');
    } else {
      score *= 0.3;
      missing.push('GDPR');
    }
  }
  if (req.hipaaRequired) {
    if (tech.compliance.hipaa) {
      reasons.push('HIPAA compliant');
    } else {
      score *= 0.3;
      missing.push('HIPAA');
    }
  }
  if (req.soc2Required) {
    if (tech.compliance.soc2) {
      reasons.push('SOC 2 compliant');
    } else {
      score *= 0.3;
      missing.push('SOC 2');
    }
  }
  if (req.pciRequired) {
    if (tech.compliance.pci) {
      reasons.push('PCI DSS compliant');
    } else {
      score *= 0.3;
      missing.push('PCI DSS');
    }
  }

  // Security features
  const securityFeatures: string[] = [];
  if (tech.compliance.security.encryption) securityFeatures.push('encryption');
  if (tech.compliance.security.auditLogs) securityFeatures.push('audit logs');
  if (tech.compliance.security.rbac) securityFeatures.push('RBAC');
  if (tech.compliance.security.mfa) securityFeatures.push('MFA');

  const securityScore =
    (tech.compliance.security.encryption ? 0.25 : 0) +
    (tech.compliance.security.auditLogs ? 0.25 : 0) +
    (tech.compliance.security.rbac ? 0.25 : 0) +
    (tech.compliance.security.mfa ? 0.25 : 0);

  if (securityFeatures.length > 0) {
    reasons.push(`Security: ${securityFeatures.join(', ')}`);
  }

  const finalScore = score * securityScore;
  let rationale = '';

  if (missing.length > 0) {
    rationale = `Missing required: ${missing.join(', ')}. `;
  }
  if (reasons.length > 0) {
    rationale += reasons.join('. ');
  }
  if (!rationale) {
    rationale = 'No compliance requirements specified';
  }

  return { score: finalScore, rationale: rationale.trim() };
}

/**
 * Score technical fit
 */
function scoreTechnical(tech: TechnologyOption, req: UserRequirements): { score: number; rationale: string } {
  let score = 0;
  const reasons: string[] = [];

  // Scalability match
  const scalabilityMatch = matchLevel(tech.technical.scalability, req.scalabilityNeeds);
  score += scalabilityMatch * 0.35;
  if (scalabilityMatch >= 0.9) {
    reasons.push(`Scalability (${tech.technical.scalability}) meets/exceeds ${req.scalabilityNeeds} needs`);
  } else if (scalabilityMatch < 0.7) {
    reasons.push(`Scalability (${tech.technical.scalability}) below ${req.scalabilityNeeds} requirement`);
  }

  // Performance match
  const performanceMatch = matchLevel(tech.technical.performance, req.performanceNeeds);
  score += performanceMatch * 0.35;
  if (performanceMatch >= 0.9) {
    reasons.push(`Performance (${tech.technical.performance}) meets/exceeds ${req.performanceNeeds} needs`);
  } else if (performanceMatch < 0.7) {
    reasons.push(`Performance (${tech.technical.performance}) below ${req.performanceNeeds} requirement`);
  }

  // Learning curve (inverse - easier is better for less experienced teams)
  const learningCurveScore = {
    'beginner': { 'easy': 1.0, 'moderate': 0.7, 'steep': 0.4, 'very-steep': 0.2 },
    'intermediate': { 'easy': 0.9, 'moderate': 1.0, 'steep': 0.7, 'very-steep': 0.4 },
    'advanced': { 'easy': 0.8, 'moderate': 0.9, 'steep': 1.0, 'very-steep': 0.7 },
    'expert': { 'easy': 0.7, 'moderate': 0.8, 'steep': 0.9, 'very-steep': 1.0 },
  };
  const lcScore = learningCurveScore[req.teamExpertise][tech.technical.learningCurve];
  score += lcScore * 0.20;
  if (lcScore >= 0.9) {
    reasons.push(`Learning curve (${tech.technical.learningCurve}) well-suited for ${req.teamExpertise} team`);
  } else if (lcScore < 0.5) {
    reasons.push(`Learning curve (${tech.technical.learningCurve}) challenging for ${req.teamExpertise} team`);
  }

  // Maturity match
  const maturityScore = {
    'bleeding-edge': { 'experimental': 1.0, 'early': 0.8, 'stable': 0.6, 'mature': 0.4, 'legacy': 0.2 },
    'modern': { 'experimental': 0.5, 'early': 0.9, 'stable': 1.0, 'mature': 0.8, 'legacy': 0.3 },
    'stable': { 'experimental': 0.2, 'early': 0.5, 'stable': 0.9, 'mature': 1.0, 'legacy': 0.6 },
    'conservative': { 'experimental': 0.1, 'early': 0.3, 'stable': 0.7, 'mature': 1.0, 'legacy': 0.8 },
  };
  const matScore = maturityScore[req.innovationVsStability][tech.technical.maturity];
  score += matScore * 0.10;
  reasons.push(`Maturity (${tech.technical.maturity}) aligns with ${req.innovationVsStability} preference`);

  const rationale = reasons.length > 0 ? reasons.join('. ') : 'Technical requirements evaluated';
  return { score, rationale: rationale.trim() };
}

/**
 * Score operational fit
 */
function scoreOperational(tech: TechnologyOption, req: UserRequirements): { score: number; rationale: string } {
  let score = 0;
  const reasons: string[] = [];

  // Hosting preference match
  if (req.hostingPreference !== 'no-preference') {
    const hostingMatch = tech.operations.hostingOptions.includes(req.hostingPreference as any);
    score += hostingMatch ? 0.4 : 0.1;
    if (hostingMatch) {
      reasons.push(`Supports ${req.hostingPreference} hosting`);
    } else {
      reasons.push(`Preferred ${req.hostingPreference} hosting not available (supports: ${tech.operations.hostingOptions.join(', ')})`);
    }
  } else {
    score += 0.4;
    reasons.push(`Flexible hosting options: ${tech.operations.hostingOptions.join(', ')}`);
  }

  // Maintenance complexity vs capability
  const maintenanceScore = {
    'minimal': { 'low': 1.0, 'medium': 0.5, 'high': 0.2 },
    'moderate': { 'low': 0.9, 'medium': 1.0, 'high': 0.6 },
    'high': { 'low': 0.8, 'medium': 0.9, 'high': 1.0 },
  };
  const maintScore = maintenanceScore[req.maintenanceCapability][tech.operations.maintenanceComplexity];
  score += maintScore * 0.4;
  if (maintScore >= 0.9) {
    reasons.push(`Maintenance (${tech.operations.maintenanceComplexity}) matches ${req.maintenanceCapability} capability`);
  } else if (maintScore < 0.5) {
    reasons.push(`Maintenance (${tech.operations.maintenanceComplexity}) exceeds ${req.maintenanceCapability} capability`);
  }

  // Monitoring complexity
  const monitoringScore = {
    'low': 1.0,
    'medium': 0.8,
    'high': 0.6,
  };
  const monScore = monitoringScore[tech.operations.monitoringComplexity];
  score += monScore * 0.2;
  reasons.push(`Monitoring complexity: ${tech.operations.monitoringComplexity}`);

  const rationale = reasons.join('. ');
  return { score, rationale: rationale.trim() };
}

/**
 * Score cost fit
 */
function scoreCost(tech: TechnologyOption, req: UserRequirements): { score: number; rationale: string } {
  let score = 0;
  const reasons: string[] = [];

  // License preference
  const licenseScore = {
    'required': { 'open-source': 1.0, 'freemium': 0.6, 'commercial': 0.2, 'enterprise-only': 0.1 },
    'preferred': { 'open-source': 1.0, 'freemium': 0.8, 'commercial': 0.5, 'enterprise-only': 0.3 },
    'neutral': { 'open-source': 0.9, 'freemium': 0.9, 'commercial': 0.9, 'enterprise-only': 0.9 },
    'commercial-ok': { 'open-source': 0.7, 'freemium': 0.8, 'commercial': 1.0, 'enterprise-only': 0.9 },
  };
  const licScore = licenseScore[req.openSourcePreference][tech.license];
  score += licScore * 0.5;
  if (licScore >= 0.9) {
    reasons.push(`License (${tech.license}) aligns with ${req.openSourcePreference} preference`);
  } else if (licScore < 0.5) {
    reasons.push(`License (${tech.license}) conflicts with ${req.openSourcePreference} preference`);
  }

  // Budget vs pricing model
  const budgetScore = {
    'minimal': { 'free': 1.0, 'freemium': 0.9, 'usage-based': 0.5, 'per-seat': 0.3, 'enterprise': 0.1, 'hybrid': 0.4 },
    'moderate': { 'free': 0.9, 'freemium': 0.9, 'usage-based': 0.9, 'per-seat': 0.8, 'enterprise': 0.4, 'hybrid': 0.8 },
    'substantial': { 'free': 0.7, 'freemium': 0.8, 'usage-based': 1.0, 'per-seat': 0.9, 'enterprise': 0.7, 'hybrid': 0.9 },
    'enterprise': { 'free': 0.5, 'freemium': 0.6, 'usage-based': 0.8, 'per-seat': 0.9, 'enterprise': 1.0, 'hybrid': 0.9 },
  };
  const budScore = budgetScore[req.budget][tech.pricing.model];
  score += budScore * 0.3;
  if (budScore >= 0.9) {
    reasons.push(`Pricing (${tech.pricing.model}) fits ${req.budget} budget`);
  } else if (budScore < 0.5) {
    reasons.push(`Pricing (${tech.pricing.model}) may strain ${req.budget} budget`);
  }

  // Free tier availability for cost-conscious users
  if (tech.pricing.freeOption && (req.budget === 'minimal' || req.budget === 'moderate')) {
    score += 0.2;
    reasons.push('Free tier available');
  } else if (tech.pricing.freeOption) {
    score += 0.1;
    reasons.push('Free tier available');
  } else {
    score += 0.1;
  }

  const rationale = reasons.length > 0 ? reasons.join('. ') : 'Cost requirements evaluated';
  return { score, rationale: rationale.trim() };
}

/**
 * Score ecosystem fit
 */
function scoreEcosystem(tech: TechnologyOption, req: UserRequirements): { score: number; rationale: string } {
  let score = 0;
  const reasons: string[] = [];

  // Community size (important for support and hiring)
  const communitySizeScore = {
    'small': 0.4,
    'medium': 0.6,
    'large': 0.8,
    'very-large': 1.0,
  };
  const commScore = communitySizeScore[tech.ecosystem.communitySize];
  score += commScore * 0.3;
  reasons.push(`${tech.ecosystem.communitySize} community`);

  // Documentation quality
  const docScore = {
    'poor': 0.3,
    'fair': 0.6,
    'good': 0.8,
    'excellent': 1.0,
  };
  const docValue = docScore[tech.ecosystem.documentation];
  score += docValue * 0.25;
  reasons.push(`${tech.ecosystem.documentation} documentation`);

  // Enterprise support (important for larger orgs)
  if (['large', 'enterprise'].includes(req.teamSize)) {
    score += tech.ecosystem.enterpriseSupport ? 0.25 : 0.05;
    if (tech.ecosystem.enterpriseSupport) {
      reasons.push(`Enterprise support available for ${req.teamSize} team`);
    } else {
      reasons.push(`No enterprise support for ${req.teamSize} team`);
    }
  } else {
    score += 0.15;
  }

  // Popularity trend
  const trendScore = {
    'declining': 0.3,
    'stable': 0.7,
    'growing': 0.9,
    'rapidly-growing': 1.0,
  };
  const trendValue = trendScore[tech.ecosystem.popularityTrend];
  score += trendValue * 0.2;
  reasons.push(`${tech.ecosystem.popularityTrend} popularity trend`);

  const rationale = reasons.join(', ');
  return { score, rationale: rationale.trim() };
}

/**
 * Score strategic fit
 */
function scoreStrategic(tech: TechnologyOption, req: UserRequirements): { score: number; rationale: string } {
  let score = 0;
  const reasons: string[] = [];

  // Vendor lock-in tolerance
  const lockInScore = {
    'none': { 'none': 1.0, 'low': 0.7, 'medium': 0.3, 'high': 0.1 },
    'low': { 'none': 1.0, 'low': 0.9, 'medium': 0.5, 'high': 0.2 },
    'medium': { 'none': 0.8, 'low': 0.9, 'medium': 1.0, 'high': 0.6 },
    'high': { 'none': 0.6, 'low': 0.7, 'medium': 0.9, 'high': 1.0 },
  };
  const lockScore = lockInScore[req.vendorLockInTolerance][tech.migration.vendorLockIn];
  score += lockScore * 0.4;
  if (lockScore >= 0.9) {
    reasons.push(`Vendor lock-in (${tech.migration.vendorLockIn}) acceptable for ${req.vendorLockInTolerance} tolerance`);
  } else if (lockScore < 0.5) {
    reasons.push(`Vendor lock-in (${tech.migration.vendorLockIn}) exceeds ${req.vendorLockInTolerance} tolerance`);
  }

  // Team size suitability
  const teamSizeMatch = tech.suitability.teamSize.includes(req.teamSize);
  score += teamSizeMatch ? 0.3 : 0.1;
  if (teamSizeMatch) {
    reasons.push(`Suitable for ${req.teamSize} teams`);
  } else {
    reasons.push(`Designed for ${tech.suitability.teamSize.join('/')} teams, not ${req.teamSize}`);
  }

  // Use case overlap
  const useCaseOverlap = req.useCases.filter(uc =>
    tech.suitability.useCases.some(tuc =>
      tuc.toLowerCase().includes(uc.toLowerCase()) ||
      uc.toLowerCase().includes(tuc.toLowerCase())
    )
  ).length;
  const useCaseScore = Math.min(useCaseOverlap / Math.max(req.useCases.length, 1), 1.0);
  score += useCaseScore * 0.3;
  if (useCaseScore >= 0.5) {
    reasons.push(`${useCaseOverlap}/${req.useCases.length} use cases matched`);
  } else if (req.useCases.length > 0) {
    reasons.push(`Limited use case overlap (${useCaseOverlap}/${req.useCases.length})`);
  }

  const rationale = reasons.length > 0 ? reasons.join('. ') : 'Strategic fit evaluated';
  return { score, rationale: rationale.trim() };
}

/**
 * Helper to match levels
 */
function matchLevel(
  techLevel: string,
  requiredLevel: string
): number {
  const levels = ['low', 'medium', 'high', 'very-high'];
  const techIndex = levels.indexOf(techLevel);
  const reqIndex = levels.indexOf(requiredLevel);

  if (techIndex >= reqIndex) {
    // Meets or exceeds requirement
    return 1.0 - (techIndex - reqIndex) * 0.1; // Slight penalty for over-engineering
  } else {
    // Below requirement
    return 0.3 + (techIndex / reqIndex) * 0.5; // Partial credit
  }
}

/**
 * Generate insights about the match
 */
function generateInsights(
  tech: TechnologyOption,
  req: UserRequirements,
  scores: ScoredTechnology['scoreBreakdown']
): { matchReasons: string[]; concerns: string[] } {
  const matchReasons: string[] = [];
  const concerns: string[] = [];

  // Compliance insights
  if (scores.compliance >= 0.8) {
    matchReasons.push('Meets all compliance requirements');
  } else if (scores.compliance < 0.5) {
    concerns.push('May not meet all compliance requirements');
  }

  // Technical insights
  if (scores.technical >= 0.8) {
    matchReasons.push('Strong technical fit for your requirements');
  }
  if (tech.technical.learningCurve === 'easy' && req.teamExpertise === 'beginner') {
    matchReasons.push('Easy to learn for your team');
  }
  if (tech.technical.learningCurve === 'very-steep' && req.teamExpertise !== 'expert') {
    concerns.push('Steep learning curve for your team');
  }

  // Operational insights
  if (scores.operational >= 0.8) {
    matchReasons.push('Aligns well with operational capabilities');
  }
  if (tech.operations.maintenanceComplexity === 'high' && req.maintenanceCapability === 'minimal') {
    concerns.push('High maintenance requirements');
  }

  // Cost insights
  if (scores.cost >= 0.8) {
    matchReasons.push('Cost-effective for your budget');
  }
  if (tech.pricing.model === 'enterprise' && req.budget === 'minimal') {
    concerns.push('May be expensive for your budget');
  }

  // Ecosystem insights
  if (scores.ecosystem >= 0.8) {
    matchReasons.push('Strong community and support');
  }
  if (tech.ecosystem.popularityTrend === 'declining') {
    concerns.push('Declining popularity trend');
  }

  // Strategic insights
  if (scores.strategic >= 0.8) {
    matchReasons.push('Good strategic fit for your organization');
  }
  if (tech.migration.vendorLockIn === 'high' && req.vendorLockInTolerance === 'none') {
    concerns.push('High vendor lock-in risk');
  }

  return { matchReasons, concerns };
}

/**
 * Get top recommendations
 */
export function getRecommendations(
  technologies: TechnologyOption[],
  requirements: UserRequirements,
  topN: number = 5
): ScoredTechnology[] {
  const scored = technologies.map(tech => scoreTechnology(tech, requirements));

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  // Assign ranks
  scored.forEach((tech, index) => {
    tech.rank = index + 1;
  });

  return scored.slice(0, topN);
}

/**
 * Compare two technologies and generate detailed comparison
 */
export function compareScores(
  tech1: ScoredTechnology,
  tech2: ScoredTechnology
): string {
  const scoreDiff = Math.abs(tech1.score - tech2.score);
  const percentDiff = ((scoreDiff / tech2.score) * 100).toFixed(0);

  const breakdownComparisons: string[] = [];

  Object.entries(tech1.scoreBreakdown).forEach(([key, value]) => {
    const otherValue = tech2.scoreBreakdown[key as keyof typeof tech2.scoreBreakdown];
    const diff = value - otherValue;
    if (Math.abs(diff) > 0.1) {
      const percentChange = ((Math.abs(diff) / otherValue) * 100).toFixed(0);
      const direction = diff > 0 ? 'better' : 'lower';
      breakdownComparisons.push(`${percentChange}% ${direction} on ${key}`);
    }
  });

  const breakdown = breakdownComparisons.length > 0
    ? breakdownComparisons.join(', ')
    : 'similar across all dimensions';

  return `Overall: ${tech1.score.toFixed(1)} vs ${tech2.score.toFixed(1)} (${percentDiff}% difference). ${breakdown}.`;
}

/**
 * Find runner-up technologies
 */
export function findRunnerUps(
  primary: ScoredTechnology[],
  allScored: ScoredTechnology[],
  limit: number = 3
): RunnerUp[] {
  const runnersUp: RunnerUp[] = [];
  const primaryIds = new Set(primary.map(t => t.id));

  // Group remaining technologies by category
  const byCategory: { [category: string]: ScoredTechnology[] } = {};
  allScored.forEach(tech => {
    if (!primaryIds.has(tech.id)) {
      if (!byCategory[tech.category]) {
        byCategory[tech.category] = [];
      }
      byCategory[tech.category].push(tech);
    }
  });

  // For each primary technology, find runner-ups in same category
  primary.forEach(primaryTech => {
    const candidates = byCategory[primaryTech.category] || [];
    const topCandidates = candidates
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    topCandidates.forEach(candidate => {
      // Generate comparison insights
      const scoreDiff = primaryTech.score - candidate.score;
      const percentDiff = ((scoreDiff / primaryTech.score) * 100).toFixed(1);

      // Find where candidate excels
      let strengthArea = '';
      let strengthDiff = 0;
      Object.entries(candidate.scoreBreakdown).forEach(([key, value]) => {
        const primaryValue = primaryTech.scoreBreakdown[key as keyof typeof primaryTech.scoreBreakdown];
        const diff = value - primaryValue;
        if (diff > strengthDiff) {
          strengthDiff = diff;
          strengthArea = key;
        }
      });

      const strengthPercent = strengthDiff > 0
        ? `${((strengthDiff / primaryTech.scoreBreakdown[strengthArea as keyof typeof primaryTech.scoreBreakdown]) * 100).toFixed(0)}%`
        : '';

      const reason = strengthDiff > 0.1
        ? `Strong ${strengthArea} capabilities (${strengthPercent} better than ${primaryTech.name})`
        : `Similar capabilities to ${primaryTech.name}`;

      runnersUp.push({
        technology: candidate,
        reason,
        scoreComparison: compareScores(candidate, primaryTech),
        alternativeTo: primaryTech.id,
      });
    });
  });

  return runnersUp.slice(0, limit * primary.length);
}

/**
 * Estimate monthly cost for a technology stack
 */
export function estimateStackCost(
  technologies: ScoredTechnology[],
  budget: UserRequirements['budget']
): string {
  const costMultipliers = {
    'minimal': 0.5,
    'moderate': 1.0,
    'substantial': 2.5,
    'enterprise': 5.0,
  };

  const baseCosts = {
    'free': 0,
    'freemium': 50,
    'usage-based': 200,
    'per-seat': 150,
    'enterprise': 500,
    'hybrid': 100,
  };

  let minCost = 0;
  let maxCost = 0;

  technologies.forEach(tech => {
    const base = baseCosts[tech.pricing.model];
    minCost += base * costMultipliers[budget];
    maxCost += base * costMultipliers[budget] * 2;
  });

  if (minCost === 0 && maxCost === 0) {
    return '$0-100/month (mostly free tiers)';
  }

  return `$${Math.round(minCost)}-${Math.round(maxCost)}/month`;
}

/**
 * Calculate overall complexity score for a stack
 */
export function calculateComplexityScore(technologies: ScoredTechnology[]): number {
  const complexityWeights = {
    'low': 0.2,
    'medium': 0.5,
    'high': 1.0,
  };

  let totalComplexity = 0;
  technologies.forEach(tech => {
    totalComplexity += complexityWeights[tech.operations.maintenanceComplexity];
    totalComplexity += complexityWeights[tech.operations.monitoringComplexity];
  });

  return totalComplexity / (technologies.length * 2);
}

/**
 * Assess risks for recommended technologies
 */
export function assessRisks(
  technologies: ScoredTechnology[],
  requirements: UserRequirements
): RiskAssessment[] {
  const risks: RiskAssessment[] = [];

  technologies.forEach(tech => {
    // Maturity risks
    if (tech.technical.maturity === 'experimental' || tech.technical.maturity === 'early') {
      risks.push({
        level: tech.technical.maturity === 'experimental' ? 'high' : 'medium',
        category: 'maturity',
        description: `${tech.name} is in ${tech.technical.maturity} maturity stage with potential breaking changes`,
        mitigation: 'Pin to specific version, maintain rollback plan, avoid in critical paths, monitor changelog closely',
      });
    }

    // Vendor lock-in risks
    if (tech.migration.vendorLockIn === 'high' && requirements.vendorLockInTolerance !== 'high') {
      risks.push({
        level: requirements.vendorLockInTolerance === 'none' ? 'high' : 'medium',
        category: 'vendor-lock-in',
        description: `${tech.name} has high vendor lock-in risk, making migration difficult`,
        mitigation: 'Use abstraction layers, document migration paths, negotiate exit terms in contract',
      });
    }

    // Cost risks
    if (tech.pricing.model === 'usage-based' || tech.pricing.model === 'enterprise') {
      if (requirements.budget === 'minimal' || requirements.budget === 'moderate') {
        risks.push({
          level: 'medium',
          category: 'cost',
          description: `${tech.name} uses ${tech.pricing.model} pricing which may lead to unexpected costs`,
          mitigation: 'Set up billing alerts, implement usage monitoring, consider pricing caps, review bills regularly',
        });
      }
    }

    // Complexity risks
    if (tech.operations.maintenanceComplexity === 'high' && requirements.maintenanceCapability === 'minimal') {
      risks.push({
        level: 'high',
        category: 'complexity',
        description: `${tech.name} has high maintenance complexity but team has minimal capability`,
        mitigation: 'Invest in training, hire specialists, consider managed alternatives, allocate dedicated ops time',
      });
    }

    // Learning curve risks
    if (tech.technical.learningCurve === 'steep' || tech.technical.learningCurve === 'very-steep') {
      if (requirements.teamExpertise === 'beginner' || requirements.teamExpertise === 'intermediate') {
        risks.push({
          level: tech.technical.learningCurve === 'very-steep' ? 'high' : 'medium',
          category: 'learning-curve',
          description: `${tech.name} has a ${tech.technical.learningCurve} learning curve for current team expertise`,
          mitigation: 'Allocate learning time, provide training budget, pair with experienced mentors, start with POC',
        });
      }
    }

    // Ecosystem risks
    if (tech.ecosystem.communitySize === 'small') {
      risks.push({
        level: 'medium',
        category: 'ecosystem',
        description: `${tech.name} has a small community, limiting support and resources`,
        mitigation: 'Budget for paid support, build internal expertise, contribute to community, have backup options',
      });
    }

    if (tech.ecosystem.popularityTrend === 'declining') {
      risks.push({
        level: 'high',
        category: 'ecosystem',
        description: `${tech.name} has declining popularity, risking future support`,
        mitigation: 'Plan migration timeline, monitor project health, identify alternatives, avoid deep integration',
      });
    }

    // Compliance risks
    if ((requirements.gdprRequired && !tech.compliance.gdpr) ||
        (requirements.hipaaRequired && !tech.compliance.hipaa) ||
        (requirements.soc2Required && !tech.compliance.soc2) ||
        (requirements.pciRequired && !tech.compliance.pci)) {
      risks.push({
        level: 'high',
        category: 'compliance',
        description: `${tech.name} may not meet required compliance standards`,
        mitigation: 'Verify compliance with vendor, implement additional controls, conduct audit, consider alternatives',
      });
    }
  });

  // Sort by risk level
  const levelOrder = { high: 0, medium: 1, low: 2 };
  risks.sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);

  return risks;
}

/**
 * Generate intelligent, context-aware "when to choose" recommendation
 */
function generateIntelligentRecommendation(
  stackName: string,
  technologies: ScoredTechnology[],
  requirements: UserRequirements,
  recommendedTechs: ScoredTechnology[]
): string {
  const parts: string[] = [];

  // Analyze the actual technologies in this stack
  const hasOpenSource = technologies.filter(t => t.license === 'open-source').length / technologies.length > 0.7;
  const hasManaged = technologies.filter(t => t.operations.hostingOptions.includes('managed')).length / technologies.length > 0.5;
  const hasMature = technologies.filter(t => t.technical.maturity === 'mature').length / technologies.length > 0.7;
  const hasEarly = technologies.filter(t => t.technical.maturity === 'early' || t.technical.maturity === 'experimental').length > 2;
  const avgComplexity = technologies.reduce((sum, t) => {
    const complexity = { low: 1, medium: 2, high: 3 }[t.operations.maintenanceComplexity];
    return sum + complexity;
  }, 0) / technologies.length;

  // Get key technology names for specificity
  const keyTechs = technologies.slice(0, 3).map(t => t.name);

  // Build context-aware recommendation based on stack characteristics
  switch (stackName) {
    case 'Cost-Optimized Stack':
      if (hasOpenSource) {
        parts.push(`Choose this if you're running a ${requirements.teamSize} team with strong DevOps capabilities`);
      }
      parts.push(`using primarily ${keyTechs.slice(0, 2).join(' and ')}`);
      if (!hasManaged) {
        parts.push(`and willing to self-host and manage infrastructure`);
      }
      if (requirements.budget === 'minimal') {
        parts.push(`Your minimal budget aligns perfectly with this open-source focused approach`);
      }
      break;

    case 'Performance-First Stack':
      parts.push(`Choose this for ${requirements.scalabilityNeeds === 'very-high' ? 'massive scale' : 'high-traffic'} applications`);
      parts.push(`where ${keyTechs[0]} provides ${technologies[0].technical.performance} performance`);
      if (requirements.teamExpertise === 'expert' || requirements.teamExpertise === 'advanced') {
        parts.push(`Your ${requirements.teamExpertise} team can handle the complexity of technologies like ${keyTechs.slice(1, 3).join(' and ')}`);
      } else {
        parts.push(`Note: This stack requires advanced expertise to operate ${keyTechs.slice(1, 3).join(' and ')} effectively`);
      }
      break;

    case 'Stability-First Stack':
      if (hasMature) {
        parts.push(`Choose this for ${requirements.industry} where reliability is paramount`);
        parts.push(`All technologies (${keyTechs.join(', ')}) are battle-tested and production-proven`);
      }
      if (requirements.innovationVsStability === 'conservative') {
        parts.push(`Matches your conservative approach - minimize risk with established solutions`);
      } else {
        parts.push(`Consider this if you value stability over cutting-edge features`);
      }
      break;

    case 'Simplicity Stack':
      if (hasManaged) {
        parts.push(`Choose this for ${requirements.teamSize} teams with ${requirements.maintenanceCapability} operational capacity`);
      }
      parts.push(`${keyTechs[0]} and ${keyTechs[1]} provide managed services that reduce maintenance burden`);
      if (avgComplexity < 1.5) {
        parts.push(`Average maintenance complexity is low - perfect for small teams moving fast`);
      }
      if (requirements.timeline === 'very-urgent' || requirements.timeline === 'urgent') {
        parts.push(`Fastest time-to-market with minimal setup required`);
      }
      break;

    case 'Innovation Stack':
      if (hasEarly) {
        parts.push(`Choose this if you're building cutting-edge ${requirements.industry} products`);
        parts.push(`where latest features in ${keyTechs.slice(0, 2).join(' and ')} provide competitive advantage`);
      }
      if (requirements.teamExpertise === 'expert') {
        parts.push(`Your expert team can navigate the risks of newer technologies`);
      } else {
        parts.push(`Warning: Requires comfort with breaking changes and limited community support`);
      }
      break;
  }

  // Add comparison to recommended stack
  const recommendedNames = recommendedTechs.slice(0, 2).map(t => t.name);
  const techOverlap = technologies.filter(t => recommendedTechs.some(rt => rt.id === t.id)).length;

  if (techOverlap < technologies.length * 0.3) {
    parts.push(`Significantly different from recommended stack (${recommendedNames.join(', ')}...)`);
  }

  return parts.join('. ') + '.';
}

/**
 * Generate alternative stacks optimized for different priorities
 */
export function generateAlternativeStacks(
  requirements: UserRequirements,
  allTechnologies: TechnologyOption[],
  recommendedStack?: ScoredTechnology[]
): AlternativeStack[] {
  const stacks: AlternativeStack[] = [];
  const recommended = recommendedStack || [];

  // Cost-Optimized Stack
  const costOptimized = allTechnologies
    .map(tech => scoreTechnology(tech, {
      ...requirements,
      budget: 'minimal',
      openSourcePreference: 'required',
    }))
    .sort((a, b) => b.score - a.score);

  const costTechs = getCategorySpread(costOptimized, 8);
  stacks.push({
    name: 'Cost-Optimized Stack',
    description: 'Minimize costs while maintaining core functionality',
    technologies: costTechs,
    tradeoffs: [
      'Manual scaling vs auto-scaling',
      'Self-managed vs fully-managed',
      'Community support vs enterprise support',
      'May require more ops expertise',
    ],
    estimatedMonthlyCost: estimateStackCost(costTechs, 'minimal'),
    whenToChoose: generateIntelligentRecommendation('Cost-Optimized Stack', costTechs, requirements, recommended),
    vsRecommended: {
      costDelta: '-60% to -80%',
      performanceDelta: '-10% to -20%',
      complexityDelta: '+20% to +40%',
    },
  });

  // Performance-First Stack
  const perfOptimized = allTechnologies
    .map(tech => scoreTechnology(tech, {
      ...requirements,
      performanceNeeds: 'very-high',
      scalabilityNeeds: 'very-high',
      budget: 'enterprise',
    }))
    .sort((a, b) => b.score - a.score);

  const perfTechs = getCategorySpread(perfOptimized, 8);
  stacks.push({
    name: 'Performance-First Stack',
    description: 'Maximize speed and scalability regardless of cost',
    technologies: perfTechs,
    tradeoffs: [
      'Higher infrastructure costs',
      'More specialized tools to manage',
      'May be over-engineered for current needs',
      'Requires advanced expertise',
    ],
    estimatedMonthlyCost: estimateStackCost(perfTechs, 'enterprise'),
    whenToChoose: generateIntelligentRecommendation('Performance-First Stack', perfTechs, requirements, recommended),
    vsRecommended: {
      costDelta: '+100% to +200%',
      performanceDelta: '+30% to +50%',
      complexityDelta: '+10% to +30%',
    },
  });

  // Stability-First Stack
  const stabilityOptimized = allTechnologies
    .map(tech => scoreTechnology(tech, {
      ...requirements,
      innovationVsStability: 'conservative',
      teamExpertise: 'intermediate',
    }))
    .sort((a, b) => b.score - a.score);

  const stabilityTechs = getCategorySpread(stabilityOptimized, 8);
  stacks.push({
    name: 'Stability-First Stack',
    description: 'Choose most mature, battle-tested technologies',
    technologies: stabilityTechs,
    tradeoffs: [
      'Less cutting-edge features',
      'May lack latest innovations',
      'Potential technical debt over time',
      'Proven track record',
    ],
    estimatedMonthlyCost: estimateStackCost(stabilityTechs, requirements.budget),
    whenToChoose: generateIntelligentRecommendation('Stability-First Stack', stabilityTechs, requirements, recommended),
    vsRecommended: {
      costDelta: '-10% to +10%',
      performanceDelta: '-5% to +5%',
      complexityDelta: '-20% to -30%',
    },
  });

  // Simplicity Stack
  const simplicityOptimized = allTechnologies
    .map(tech => scoreTechnology(tech, {
      ...requirements,
      maintenanceCapability: 'minimal',
      hostingPreference: 'managed',
    }))
    .sort((a, b) => b.score - a.score);

  const simplicityTechs = getCategorySpread(simplicityOptimized, 6);
  stacks.push({
    name: 'Simplicity Stack',
    description: 'Minimize number of technologies and operational complexity',
    technologies: simplicityTechs,
    tradeoffs: [
      'Fewer specialized tools',
      'All-in-one solutions may be less flexible',
      'Some vendor lock-in acceptable',
      'Faster time to market',
    ],
    estimatedMonthlyCost: estimateStackCost(simplicityTechs, requirements.budget),
    whenToChoose: generateIntelligentRecommendation('Simplicity Stack', simplicityTechs, requirements, recommended),
    vsRecommended: {
      costDelta: '-20% to +20%',
      performanceDelta: '-10% to +10%',
      complexityDelta: '-40% to -60%',
    },
  });

  // Innovation Stack (if user is open to it)
  if (requirements.innovationVsStability === 'bleeding-edge' || requirements.innovationVsStability === 'modern') {
    const innovationOptimized = allTechnologies
      .map(tech => scoreTechnology(tech, {
        ...requirements,
        innovationVsStability: 'bleeding-edge',
        teamExpertise: 'expert',
      }))
      .sort((a, b) => b.score - a.score);

    const innovationTechs = getCategorySpread(innovationOptimized, 8);
    stacks.push({
      name: 'Innovation Stack',
      description: 'Latest and greatest technologies with cutting-edge features',
      technologies: innovationTechs,
      tradeoffs: [
        'Higher risk of breaking changes',
        'Smaller communities and less support',
        'Potential bugs and instability',
        'Access to latest features',
      ],
      estimatedMonthlyCost: estimateStackCost(innovationTechs, requirements.budget),
      whenToChoose: generateIntelligentRecommendation('Innovation Stack', innovationTechs, requirements, recommended),
      vsRecommended: {
        costDelta: '-10% to +10%',
        performanceDelta: '+10% to +30%',
        complexityDelta: '+30% to +50%',
      },
    });
  }

  return stacks;
}

/**
 * Helper to get diverse category spread
 */
function getCategorySpread(scored: ScoredTechnology[], limit: number): ScoredTechnology[] {
  const result: ScoredTechnology[] = [];
  const categoriesSeen = new Set<string>();

  // First pass: get top from each category
  scored.forEach(tech => {
    if (!categoriesSeen.has(tech.category) && result.length < limit) {
      result.push(tech);
      categoriesSeen.add(tech.category);
    }
  });

  // Second pass: fill remaining slots with highest scores
  scored.forEach(tech => {
    if (result.length < limit && !result.find(t => t.id === tech.id)) {
      result.push(tech);
    }
  });

  return result.slice(0, limit);
}

/**
 * Generate comprehensive recommendations with runner-ups, alternatives, and risks
 */
export function generateEnhancedRecommendations(
  technologies: TechnologyOption[],
  requirements: UserRequirements,
  topN: number = 5
): EnhancedRecommendationResult {
  // Score all technologies
  const allScored = technologies.map(tech => scoreTechnology(tech, requirements));
  allScored.sort((a, b) => b.score - a.score);

  // Assign ranks
  allScored.forEach((tech, index) => {
    tech.rank = index + 1;
  });

  // Get primary recommendations
  const primary = allScored.slice(0, topN);

  // Find runner-ups
  const runnersUp = findRunnerUps(primary, allScored, 3);

  // Generate alternative stacks (pass primary stack for comparison)
  const alternativeStacks = generateAlternativeStacks(requirements, technologies, primary);

  // Assess risks
  const risks = assessRisks(primary, requirements);

  // Build filtering criteria description
  const filteringCriteria: string[] = [];
  if (requirements.gdprRequired) filteringCriteria.push('GDPR compliance');
  if (requirements.hipaaRequired) filteringCriteria.push('HIPAA compliance');
  if (requirements.soc2Required) filteringCriteria.push('SOC2 compliance');
  if (requirements.pciRequired) filteringCriteria.push('PCI compliance');
  if (requirements.openSourcePreference === 'required') filteringCriteria.push('Open source only');
  if (requirements.hostingPreference !== 'no-preference') {
    filteringCriteria.push(`${requirements.hostingPreference} hosting`);
  }

  return {
    primary,
    runnersUp,
    alternativeStacks,
    risks,
    metadata: {
      totalEvaluated: allScored.length,
      filteringCriteria,
      scoreRange: {
        min: allScored[allScored.length - 1]?.score || 0,
        max: allScored[0]?.score || 0,
      },
    },
  };
}
